import { useQuery } from '@tanstack/react-query'
import { useState } from 'react'
import { skuDashboardQuery, type SkuCountTile, type SkuPairTile, type SkuTile } from '../../api/easeedeskDashboards'
import { Content } from '../../ui/Shell'
import { RANGES, rangeOf, type RangeName } from './range'
import { NotInstalled } from './WallHeader'

/**
 * "Sku Dashboard" - `easeedesk-dashboards/Sku::index` + `get_dashboard_data`,
 * macheta `views/version-3/dashboards/easeedesk/sku/dashboard.php` si casutele
 * pe care le desena `SkuDashboard` din `public/js/dashboards/easeedesk.js`.
 *
 * Aceleasi treizeci si una de casute, in aceeasi ordine si cu aceleasi
 * cuvinte, pe aceleasi masuri (`.dashboard_col2` si celelalte, luate din
 * macheta). Culoarea unei casute simple se face din cifra ei si din scara pe
 * care i-o trimite capatul - `queue`, `unassigned`, `lost` sau `plain` - in
 * loc sa fie o insiruire de `case` in ecran, ca in `drawElements`.
 *
 * GRAFICELE NU SE ADUC INAPOI, si nu e o scapare: erau deja stinse in portal.
 * Randul de sus al machetei (`#manualCreatedChart`, `#deliveredChart`,
 * `#soldChart`, `#sdoGraph`, `#manualFinishedChart`, `#automatedFinishedChart`)
 * e comentat cu totul, `drawSpeedometer` din JS la fel, iar capatul lor
 * (`Sku::get_dashboard_chart_data`) e comentat in controller. amCharts a iesit
 * din portal pe 6 septembrie 2026 si nu se intoarce pentru casute care nu se
 * deseneaza.
 */

const STYLE = `
.od-sku-board .dashboard_col2 {
  width: 20%; float: left; position: relative; min-height: 1px;
  padding-right: 15px; padding-left: 15px; text-align: center; margin-top: 15px;
}
.od-sku-board .dashboard_number2 {
  float: left; padding: 10px; border-top-left-radius: 10px; border-bottom-left-radius: 10px;
  font-size: 20px; height: 70px; line-height: 50px; cursor: default;
}
.od-sku-board .dashboard_text2 {
  padding: 10px; border-top-right-radius: 10px; border-bottom-right-radius: 10px;
  overflow: hidden; font-size: 20px; height: 70px; line-height: 50px;
  white-space: nowrap; text-overflow: ellipsis; cursor: help;
}
.od-sku-board .dashboard_left_content { float: left; padding: 10px; font-size: 20px; cursor: default; }
.od-sku-board .dashboard_right_content {
  overflow: hidden; padding: 10px; font-size: 18px; white-space: nowrap;
  text-overflow: ellipsis; cursor: help;
}
.od-sku-board .dashboard_top_content { height: 70px; line-height: 50px; }
.od-sku-board .dashboard_bottom_content {
  height: 50px; line-height: 30px; font-size: 20px; border-top: 1px solid #e7e7e7; cursor: default;
}
`

/** Pragurile din `drawElements`: sub primul e verde, apoi galben, apoi rosu. */
const SCALES: Record<string, { warning: number; danger: number } | null> = {
  queue: { warning: 5, danger: 10 },
  unassigned: { warning: 1, danger: 5 },
  lost: { warning: 10, danger: 20 },
  plain: null,
}

export function toneOf(tile: SkuCountTile): string {
  const scale = SCALES[tile.scale] ?? null
  if (scale === null) return 'bg-primary'
  if (tile.value >= scale.danger) return 'bg-danger'
  if (tile.value >= scale.warning) return 'bg-warning'

  return 'bg-success'
}

function CountTile({ tile }: { tile: SkuCountTile }) {
  const tone = toneOf(tile)

  return (
    <div className="dashboard_col2" data-tile={tile.key}>
      <div className={`dashboard_number2 ${tone}`}>{tile.value}</div>
      <div className={`dashboard_text2 ${tone}`} title={tile.label}>
        {tile.label}
      </div>
    </div>
  )
}

function PairTile({ tile }: { tile: SkuPairTile }) {
  return (
    <div className="dashboard_col2" data-tile={tile.key}>
      <div className="dashboard_left_content dashboard_top_content bg-primary" style={{ borderTopLeftRadius: 10 }}>
        {tile.top}
      </div>
      <div className="dashboard_right_content dashboard_top_content bg-primary" style={{ borderTopRightRadius: 10 }} title={tile.label}>
        {tile.label}
      </div>
      <div className="dashboard_left_content dashboard_bottom_content bg-primary" style={{ borderBottomLeftRadius: 10 }}>
        {tile.bottom_label}
      </div>
      <div className="dashboard_right_content dashboard_bottom_content bg-primary" style={{ borderBottomRightRadius: 10 }}>
        {tile.bottom}
      </div>
    </div>
  )
}

export function SkuDashboardPage() {
  const [rangeName, setRangeName] = useState<RangeName>('Last 24 Hours')
  const q = useQuery(skuDashboardQuery(rangeOf(rangeName)))
  const page = q.data

  return (
    <Content>
      <style>{STYLE}</style>
      <div className="od-sku-board">
        <div className="row" style={{ marginBottom: 20 }}>
          <div className="col-md-6">
            <h4>{page?.title ?? 'Sku Dashboard'}</h4>
          </div>
          <div className="col-md-2 pull-right">
            <select
              className="form-control"
              aria-label="Date range"
              value={rangeName}
              onChange={(e) => setRangeName(e.target.value as RangeName)}
            >
              {RANGES.map((name) => (
                <option key={name} value={name}>
                  {name}
                </option>
              ))}
            </select>
          </div>
        </div>

        {page && !page.ready ? <NotInstalled what="Sku dashboard" /> : null}

        <div className="row" id="sku-numbers">
          {(page?.tiles ?? []).map((tile: SkuTile) =>
            tile.kind === 'count' ? <CountTile key={tile.key} tile={tile} /> : <PairTile key={tile.key} tile={tile} />
          )}
        </div>
      </div>
    </Content>
  )
}
